// Primer día guiado (ola 1 · W2d, propuesta 12.8): llamadas a /api/users/onboarding*
// y helpers puros compartidos por los pasos (formato de fecha, línea del examen,
// respuestas del test de calibración). Sin estado global.
import apiClient from '../../services/api';
import { API } from '../../config/api';

export const PASOS = ['fecha', 'ritual', 'calibracion', 'hoy'];
export const PASO_TITULOS = {
  fecha: 'Tu examen',
  ritual: 'Tu ritual',
  calibracion: 'Tu punto de partida',
  hoy: 'Tu sesión de hoy',
};

// Una comprobación por pestaña y alumna: se recuerda el user_id ya comprobado en
// sessionStorage (no el token: rota con el refresh y re-armaría la puerta).
export const GATE_KEY = 'onboarding_v2_gate';
// Las salidas del flujo no esperan a la red: se registran en segundo plano con este tope.
export const SALIDA_TIMEOUT_MS = 4000;

export const getOnboarding = (detalle = false, opts = {}) =>
  apiClient.get(`${API}/users/onboarding${detalle ? '?detalle=1' : ''}`, opts).then((r) => r.data);

export const postStep = (body, opts = {}) =>
  apiClient.post(`${API}/users/onboarding/step`, body, opts).then((r) => r.data);

export const getSesionHoy = () =>
  apiClient.get(`${API}/users/onboarding/hoy`).then((r) => r.data);

export const dismissOnboarding = (opts = {}) => postStep({ action: 'dismiss' }, opts);

export const gateYaComprobada = (userId) => {
  if (!userId) return true;
  try {
    return sessionStorage.getItem(GATE_KEY) === String(userId);
  } catch (_e) {
    return true; // sin sessionStorage (modo privado estricto): no redirigir nunca
  }
};

export const marcarGateComprobada = (userId) => {
  if (!userId) return;
  try {
    sessionStorage.setItem(GATE_KEY, String(userId));
  } catch (_e) {
    // sin almacenamiento: la puerta simplemente no se recuerda
  }
};

// "2026-12-01" → "1 de diciembre de 2026" (mediodía local para no saltar de día)
export const formatearFecha = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' });
};

export const textoDiasRestantes = (n) => {
  if (n === null || n === undefined) return '';
  if (n < 0) return 'El examen ya se ha celebrado';
  if (n === 0) return 'Tu examen es hoy';
  if (n === 1) return 'Queda 1 día';
  return `Quedan ${n} días`;
};

const num = (n, signo = false) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return '?';
  const s = Number(n).toLocaleString('es-ES', { maximumFractionDigits: 2 });
  return signo && Number(n) > 0 ? `+${s}` : s;
};

// Línea fija del paso 1, derivada de exam_config (nunca hardcodeada en el JSX).
export const lineaExamen = (cfg) => {
  const c = cfg || {};
  const total = c.total_questions ?? 100;
  const reserva = c.reserve_questions ?? 10;
  const minutos = c.time_limit_minutes ?? 150;
  const acierto = c.points_correct ?? 0.6;
  const error = c.penalty_incorrect ?? -0.15;
  const blanco = c.penalty_blank ?? 0;
  const corte = c.pass_score ?? 30;
  const max = c.max_score ?? 60;
  return `${total + reserva} preguntas (${total} + ${reserva} de reserva) · ${minutos} min · `
    + `${num(acierto, true)} acierto / ${num(error)} error / ${num(blanco)} en blanco · `
    + `corte ${num(corte)} de ${num(max)}`;
};

// Mismo contrato que ExamPage.answerArray: selected_option_id = option_id (o índice)
// y confianza opcional (baja|media|alta) solo en las respondidas.
export const construirRespuestas = (questions, answers, confidences) =>
  (questions || []).map((q, i) => {
    const idx = answers?.[q.question_id];
    let selected = null;
    if (idx !== undefined && idx !== null) {
      const opt = (q.options || [])[idx];
      if (opt !== undefined) {
        selected = typeof opt === 'object' && opt !== null && opt.option_id !== undefined ? opt.option_id : idx;
      }
    }
    const confianza = confidences?.[q.question_id];
    return {
      question_id: q.question_id,
      selected_option_id: selected,
      question_index: i + 1,
      ...(selected !== null && confianza ? { confianza } : {}),
    };
  });

export const letra = (i) => String.fromCharCode(65 + i);
